import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Spinner } from '../Spinner';
import { TranslatingAnimation } from './TranslatingAnimation';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
`;

const ContentRelative = styled.div`
  position: relative;
`;

const TranslatingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  /* > z-index logo hotelu (20) i innych elementów karty, by przykryć też logo/cover. */
  z-index: 50;
  border-radius: 12px;
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(17, 24, 39, 0.72)' : 'rgba(255, 255, 255, 0.72)')};
  backdrop-filter: blur(2px);
  /* Overlay jest CZYSTO WIZUALNY (dim + animacja „Translating"). NIE może przechwytywać
     kliknięć: gdyby status tłumaczenia utknął na „pending" (np. trigger padł po ustawieniu
     pending), sekcja zostałaby TRWALE nieedytowalna bez ścieżki wyjścia. pointer-events:none
     przepuszcza kliki do treści pod spodem, zachowując tylko wygląd. */
  pointer-events: none;
`;

// Pasek zatwierdzania zmian przypięty do sekcji (sticky do dołu podczas scrolla):
// tag „Niezapisane zmiany: <sekcja>" + Anuluj + Zapisz. Pojawia się dopiero gdy sekcja jest dirty.
const Footer = styled.div`
  position: sticky;
  bottom: 16px;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
  padding: 10px 14px;
  border-radius: 12px;
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(6, 78, 59, 0.96)' : '#f0fdf4')};
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? 'rgba(4, 120, 87, 0.45)' : '#bbf7d0')};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.14);
  animation: sectionFadeIn 0.15s ease;

  @keyframes sectionFadeIn {
    from { opacity: 0; transform: translateY(-4px); }
    to { opacity: 1; transform: none; }
  }
`;

const SectionTag = styled.span`
  margin-right: auto;
  font-size: 13px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#d1fae5' : '#065f46')};

  strong { font-weight: 300; }
`;

const SaveBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 22px;
  background: linear-gradient(180deg, #047857 0%, #064e3b 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 300;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) { filter: brightness(1.08); box-shadow: 0 4px 12px rgba(4, 120, 87, 0.3); }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const CancelBtn = styled.button`
  padding: 9px 18px;
  background: transparent;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#6b7280')};
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#d1d5db')};
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) { background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f3f4f6')}; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

interface SectionCardProps {
  children: ReactNode;
  dirty: boolean;
  saving: boolean;
  onSave: () => void;
  // Odrzucenie niezapisanych zmian sekcji (przywraca stan z danych).
  onCancel?: () => void;
  // Etykieta sekcji pokazywana w pasku (np. „Dane podstawowe").
  label?: string;
  // Gdy true — treść sekcji przykrywa wyśrodkowana animacja „Translating".
  translating?: boolean;
  // Hak dla testów e2e — pozwala zawęzić akcje (edycja/Zapisz) do konkretnej sekcji.
  testId?: string;
}

// Sekcja profilu (hotel / spa / outlet): treść + (w trakcie tłumaczenia) animowany overlay,
// oraz pasek Zapisz/Anuluj przypięty do sekcji (sticky), pojawiający się gdy są niezapisane zmiany.
export const SectionCard: FC<SectionCardProps> = ({
  children,
  dirty,
  saving,
  onSave,
  onCancel,
  label,
  translating,
  testId,
}) => {
  const { t } = useTranslation();

  return (
    <Wrap data-testid={testId}>
      <ContentRelative>
        {children}
        {translating && (
          <TranslatingOverlay>
            <TranslatingAnimation />
          </TranslatingOverlay>
        )}
      </ContentRelative>
      {dirty && (
        <Footer>
          <SectionTag>
            {t('common.unsavedChanges', 'Niezapisane zmiany')}
            {label ? ': ' : ''}
            {label && <strong>{label}</strong>}
          </SectionTag>
          {onCancel && (
            <CancelBtn type="button" onClick={onCancel} disabled={saving}>
              {t('common.cancel')}
            </CancelBtn>
          )}
          <SaveBtn type="button" onClick={onSave} disabled={saving}>
            {saving && <Spinner size="sm" />}
            {saving ? t('common.saving') : t('common.save')}
          </SaveBtn>
        </Footer>
      )}
    </Wrap>
  );
};
